import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'

/**
 * Triggers a callback when the user refocuses on the current route or navigates to a route.
 * Automatically ensures callback fires when:
 * 1. Browser tab becomes active AND pathname matches
 * 2. Route navigation occurs (pathname changes)
 *
 * @param callback Function to run on page focus or route change
 */
export const usePageFocusRefetch = (callback: () => void) => {
  const location = useLocation()
  const lastPathRef = useRef(location.pathname)
  const isInitialMountRef = useRef(true)
  const lastCallbackTimeRef = useRef(0)
  const lastCalledPathRef = useRef<string>('')

  useEffect(() => {
    const handleVisibilityChange = () => {
      const isFocused = document.visibilityState === 'visible'
      const isSamePage = lastPathRef.current === location.pathname
      const now = Date.now()

      // Prevent multiple calls within 2 seconds and ensure we haven't already called for this path
      if (
        isFocused &&
        isSamePage &&
        now - lastCallbackTimeRef.current > 2000 &&
        lastCalledPathRef.current !== location.pathname
      ) {
        console.log('Page focus detected:', location.pathname)
        lastCallbackTimeRef.current = now
        lastCalledPathRef.current = location.pathname
        callback()
      }

      lastPathRef.current = location.pathname
    }

    // Handle route navigation (pathname changes)
    if (!isInitialMountRef.current) {
      const now = Date.now()

      // Prevent multiple calls within 1 second for route changes and ensure we haven't already called for this path
      if (
        now - lastCallbackTimeRef.current > 1000 &&
        lastCalledPathRef.current !== location.pathname
      ) {
        console.log('Route navigation detected:', location.pathname)
        lastCallbackTimeRef.current = now
        lastCalledPathRef.current = location.pathname
        callback()
      }
    } else {
      isInitialMountRef.current = false
    }

    lastPathRef.current = location.pathname

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [location.pathname, callback])
}
